// src/components/Atelier3/Activite4/MeasureFusionWorkspace.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  X,
  GitMerge,
  EyeOff,
  Flag,
  Check,
  Pencil,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Users,
} from 'lucide-react';
import api from '../../../api/apiClient';

const typeMeta = {
  fuse: { icon: GitMerge, badge: 'bg-blue-100 text-blue-700', rail: 'border-l-blue-500' },
  cover: { icon: EyeOff, badge: 'bg-amber-100 text-amber-700', rail: 'border-l-amber-500' },
  conflict: { icon: Flag, badge: 'bg-red-100 text-red-700', rail: 'border-l-red-500' },
};

const decisionBadge = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-slate-200 text-slate-500',
  edited: 'bg-purple-100 text-purple-700',
};

const CATEGORY_IDS = ['governance', 'protection', 'defense', 'resilience', 'other'];
const PRIORITY_IDS = ['high', 'medium', 'low'];

// Read a measure title that may be a plain string or a localized { fr, en } object.
const displayTitle = (measure, fallback) => {
  if (!measure) return fallback || '—';
  const raw = measure.title ?? measure.name;
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (raw && typeof raw === 'object') return raw.fr || raw.en || fallback || '—';
  return fallback || '—';
};

/**
 * Modal workspace for AI-suggested ecosystem measure fusion.
 * Fetches proposals from POST /api/ai/suggest-ecosystem-fusion, lets the
 * user accept / edit / reject each proposal grouped by stakeholder, then
 * hands the decided actions back to the parent via onApplyFusion.
 */
const MeasureFusionWorkspace = ({
  isOpen,
  analysisId,
  measures,
  onClose,
  onApplyFusion,
}) => {
  const { t, i18n } = useTranslation();
  const [proposals, setProposals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  const categoryLabel = (id) =>
    t(`workshop4.operationalScenarios.measureForm.options.categories.${id}`, id);
  const priorityLabel = (id) =>
    t(`workshop4.operationalScenarios.measureForm.options.priorities.${id}`, id);

  // Fetch proposals each time the modal opens.
  useEffect(() => {
    if (!isOpen || !analysisId) return;
    let cancelled = false;
    const fetchProposals = async () => {
      setIsLoading(true);
      setError(null);
      setProposals([]);
      setEditingKey(null);
      try {
        const language = i18n.language?.startsWith('en') ? 'en' : 'fr';
        const response = await api.post('/ai/suggest-ecosystem-fusion', {
          analysisId,
          language,
        });
        if (cancelled) return;
        const list = response?.data?.proposals || [];
        setProposals(
          list.map((p, idx) => ({
            key: `proposal-${idx}`,
            type: p.type || 'fuse',
            stakeholderId: p.stakeholderId || null,
            stakeholderName: p.stakeholderName || '',
            sourceMeasureIds: p.sourceMeasureIds || [],
            mergedTitle: p.mergedTitle || '',
            mergedDescription: p.mergedDescription || '',
            category: p.category || 'governance',
            priority: p.priority || 'medium',
            coveringMeasureId: p.coveringMeasureId || null,
            confidence: p.confidence || null,
            justification: p.justification || '',
            decision: 'pending',
            draft: {
              title: p.mergedTitle || '',
              description: p.mergedDescription || '',
              category: p.category || 'governance',
              priority: p.priority || 'medium',
            },
          })),
        );
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.data?.message ||
            e?.message ||
            t('workshop3.activity4.fusion.errors.loadError', 'Erreur lors du chargement des suggestions.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchProposals();
    return () => {
      cancelled = true;
    };
  }, [isOpen, analysisId, i18n.language, t]);

  const measureById = useMemo(() => {
    const map = new Map();
    (measures || []).forEach((m) => {
      if (m.id) map.set(String(m.id), m);
      if (m._id) map.set(String(m._id), m);
    });
    return map;
  }, [measures]);

  const grouped = useMemo(() => {
    const order = [];
    const groups = {};
    proposals.forEach((p) => {
      const name = p.stakeholderName || t('workshop3.activity4.fusion.unknownStakeholder', 'Partie prenante inconnue');
      if (!groups[name]) {
        groups[name] = [];
        order.push(name);
      }
      groups[name].push(p);
    });
    return order.map((name) => ({ name, items: groups[name] }));
  }, [proposals, t]);

  const decidedCount = proposals.filter((p) => p.decision !== 'pending').length;
  const allDecided = proposals.length > 0 && decidedCount === proposals.length;

  const setDecision = (key, decision) => {
    setProposals((prev) => prev.map((p) => (p.key === key ? { ...p, decision } : p)));
    if (editingKey === key) setEditingKey(null);
  };

  const setDraftField = (key, field, value) => {
    setProposals((prev) =>
      prev.map((p) => (p.key === key ? { ...p, draft: { ...p.draft, [field]: value } } : p)),
    );
  };

  const saveEdit = (key) => {
    setProposals((prev) =>
      prev.map((p) => (p.key === key ? { ...p, decision: 'edited' } : p)),
    );
    setEditingKey(null);
  };

  const confidenceText = (value) => {
    if (!value) return null;
    const lower = String(value).toLowerCase();
    if (lower === 'high') return t('workshop3.activity4.fusion.confidenceHigh', 'Haute');
    if (lower === 'medium') return t('workshop3.activity4.fusion.confidenceMedium', 'Moyenne');
    if (lower === 'low') return t('workshop3.activity4.fusion.confidenceLow', 'Faible');
    return value;
  };

  const typeLabel = (type) => {
    if (type === 'fuse') return t('workshop3.activity4.fusion.typeFuse', 'Fusion');
    if (type === 'cover') return t('workshop3.activity4.fusion.typeCover', 'Couverture');
    return t('workshop3.activity4.fusion.typeConflict', 'Conflit');
  };

  const decisionLabel = (decision) => {
    if (decision === 'accepted') return t('workshop3.activity4.fusion.accepted', 'Acceptée');
    if (decision === 'rejected') return t('workshop3.activity4.fusion.rejected', 'Refusée');
    if (decision === 'edited') return t('workshop3.activity4.fusion.edited', 'Modifiée');
    return t('workshop3.activity4.fusion.pending', 'En attente');
  };

  const renderSources = (proposal) => (
    <ul className="mt-1.5 space-y-1">
      {(proposal.sourceMeasureIds || []).map((id, i) => {
        const m = measureById.get(String(id));
        const isCovering = proposal.type === 'cover' && String(id) === String(proposal.coveringMeasureId);
        return (
          <li key={`${proposal.key}-src-${id}-${i}`} className="text-xs text-slate-600 flex items-start gap-1.5">
            <span
              className={`mt-0.5 px-1 rounded font-mono text-[10px] flex-shrink-0 ${
                isCovering ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
              }`}
            >
              {isCovering ? t('workshop3.activity4.fusion.covering', 'couvrante') : `#${i + 1}`}
            </span>
            <span className="min-w-0">
              <span className="font-medium text-slate-700">{displayTitle(m, String(id))}</span>
              {m?.description && typeof m.description === 'string' && (
                <span className="block text-[11px] text-slate-400 break-words whitespace-normal">{m.description}</span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );

  const handleApply = () => {
    const actions = [];
    proposals.forEach((p) => {
      if (p.decision === 'rejected' || p.decision === 'pending') return;
      if (p.type === 'fuse') {
        actions.push({
          type: 'fuse',
          stakeholderId: p.stakeholderId,
          stakeholderName: p.stakeholderName,
          sourceMeasureIds: p.sourceMeasureIds,
          mergedMeasure: {
            title: p.draft.title || p.mergedTitle,
            description: p.draft.description || p.mergedDescription,
            category: p.draft.category || 'governance',
            priority: p.draft.priority || 'medium',
            stakeholders: p.stakeholderId ? [p.stakeholderId] : [],
          },
        });
      } else if (p.type === 'cover') {
        actions.push({
          type: 'cover',
          stakeholderId: p.stakeholderId,
          stakeholderName: p.stakeholderName,
          sourceMeasureIds: p.sourceMeasureIds,
          coveringMeasureId: p.coveringMeasureId,
        });
      } else {
        actions.push({
          type: 'conflict',
          stakeholderId: p.stakeholderId,
          stakeholderName: p.stakeholderName,
          sourceMeasureIds: p.sourceMeasureIds,
        });
      }
    });
    onApplyFusion(actions);
  };

  if (!isOpen) return null;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-slate-500">
          <Loader2 size={32} className="animate-spin text-purple-600" />
          <p className="mt-4 text-sm font-medium">
            {t('workshop3.activity4.fusion.loading', "L'Assistant ACG analyse vos mesures...")}
          </p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600">
            <AlertTriangle size={24} />
          </div>
          <p className="mt-4 text-sm font-semibold text-slate-800">
            {t('workshop3.activity4.fusion.errors.title', 'Suggestions indisponibles')}
          </p>
          <p className="mt-1 text-xs text-slate-500 max-w-md">{error}</p>
          <p className="mt-2 text-[11px] text-slate-400">
            {t('workshop3.activity4.fusion.errors.retryHint', 'Fermez puis rouvrez la fenêtre pour réessayer.')}
          </p>
        </div>
      );
    }
    if (!proposals.length) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
            <ShieldCheck size={24} />
          </div>
          <p className="mt-4 text-sm font-semibold text-slate-800">
            {t('workshop3.activity4.fusion.noProposals', 'Aucune fusion suggérée')}
          </p>
          <p className="mt-1 text-xs text-slate-500 max-w-md">
            {t('workshop3.activity4.fusion.noProposalsHint', 'Vos mesures ne présentent pas de redondance détectée.')}
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        {grouped.map((group) => (
          <div key={group.name}>
            <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs">
                <Users size={13} />
                {group.name}
              </span>
              <span className="text-xs font-normal text-slate-400">
                {t('workshop3.activity4.fusion.proposalCount', '{{count}} proposition(s)', { count: group.items.length })}
              </span>
            </h4>
            <div className="space-y-3">
              {group.items.map((p) => {
                const meta = typeMeta[p.type] || typeMeta.fuse;
                const Icon = meta.icon;
                const isEditing = editingKey === p.key;
                return (
                  <div
                    key={p.key}
                    className={`bg-white rounded-xl shadow-sm border border-slate-200 border-l-4 p-4 ${meta.rail}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${meta.badge}`}>
                        <Icon size={12} /> {typeLabel(p.type)}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${decisionBadge[p.decision] || decisionBadge.pending}`}>
                        {decisionLabel(p.decision)}
                      </span>
                      {p.confidence && (
                        <span className="text-[10px] text-slate-400">
                          {t('workshop3.activity4.fusion.confidence', 'confiance')} : {confidenceText(p.confidence)}
                        </span>
                      )}
                    </div>

                    {p.justification && (
                      <p className="mt-2 text-xs text-slate-600 italic border-l-2 border-purple-300 pl-2">
                        {p.justification}
                      </p>
                    )}

                    <div className="mt-3">
                      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                        {t('workshop3.activity4.fusion.involvedMeasures', 'Mesures concernées ({{count}})', { count: (p.sourceMeasureIds || []).length })}
                      </p>
                      {renderSources(p)}
                    </div>

                    {p.type === 'fuse' && (
                      <div className="mt-3 rounded-lg bg-blue-50/60 border border-blue-200 p-3">
                        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 mb-2">
                          <GitMerge size={12} />
                          {t('workshop3.activity4.fusion.mergedPreview', 'Mesure fusionnée')}
                        </p>
                        {isEditing ? (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-[11px] font-medium text-slate-600 mb-0.5">
                                {t('workshop3.activity4.fusion.fields.title', 'Titre')}
                              </label>
                              <input
                                type="text"
                                value={p.draft.title}
                                onChange={(e) => setDraftField(p.key, 'title', e.target.value)}
                                className="w-full text-sm font-medium border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-medium text-slate-600 mb-0.5">
                                {t('workshop3.activity4.fusion.fields.description', 'Description')}
                              </label>
                              <textarea
                                value={p.draft.description}
                                rows={2}
                                onChange={(e) => setDraftField(p.key, 'description', e.target.value)}
                                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[11px] font-medium text-slate-600 mb-0.5">
                                  {t('workshop3.activity4.fusion.fields.category', 'Catégorie')}
                                </label>
                                <select
                                  value={p.draft.category}
                                  onChange={(e) => setDraftField(p.key, 'category', e.target.value)}
                                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                                >
                                  {CATEGORY_IDS.map((id) => (
                                    <option key={id} value={id}>{categoryLabel(id)}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-slate-600 mb-0.5">
                                  {t('workshop3.activity4.fusion.fields.priority', 'Priorité')}
                                </label>
                                <select
                                  value={p.draft.priority}
                                  onChange={(e) => setDraftField(p.key, 'priority', e.target.value)}
                                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                                >
                                  {PRIORITY_IDS.map((id) => (
                                    <option key={id} value={id}>{priorityLabel(id)}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button
                                type="button"
                                onClick={() => saveEdit(p.key)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                              >
                                <Check size={13} />
                                {t('workshop3.activity4.fusion.saveEdit', 'Enregistrer')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingKey(null)}
                                className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                              >
                                {t('common.cancel', 'Annuler')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm font-semibold text-slate-800">
                              {p.decision === 'pending' ? p.mergedTitle : p.draft.title}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-600">
                              {p.decision === 'pending' ? p.mergedDescription : p.draft.description}
                            </p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              <span className="inline-flex px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-medium text-slate-600">
                                {categoryLabel(p.decision === 'pending' ? p.category : p.draft.category)}
                              </span>
                              <span className="inline-flex px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-medium text-slate-600">
                                {priorityLabel(p.decision === 'pending' ? p.priority : p.draft.priority)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {p.type !== 'conflict' && !isEditing && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setDecision(p.key, 'accepted')}
                          disabled={p.decision === 'accepted'}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Check size={13} />
                          {t('workshop3.activity4.fusion.accept', 'Accepter')}
                        </button>
                        {p.type === 'fuse' && (
                          <button
                            type="button"
                            onClick={() => setEditingKey(p.key)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                          >
                            <Pencil size={13} />
                            {t('workshop3.activity4.fusion.edit', 'Modifier')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDecision(p.key, 'rejected')}
                          disabled={p.decision === 'rejected'}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X size={13} />
                          {t('workshop3.activity4.fusion.reject', 'Refuser')}
                        </button>
                      </div>
                    )}
                    {p.type === 'conflict' && p.decision === 'pending' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setDecision(p.key, 'accepted')}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        >
                          <Check size={13} />
                          {t('workshop3.activity4.fusion.acknowledge', "J'ai pris connaissance")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDecision(p.key, 'rejected')}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <X size={13} />
                          {t('workshop3.activity4.fusion.reject', 'Refuser')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-auto">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative w-full max-w-5xl my-8 mx-auto animate-in fade-in zoom-in-95 duration-200 z-[10000]">
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <GitMerge size={18} />
                  {t('workshop3.activity4.fusion.modalTitle', "Fusionner par l'Assistant ACG")}
                </h3>
                <p className="text-purple-100 text-xs mt-0.5">
                  {t('workshop3.activity4.fusion.modalSubtitle', 'Vérifiez chaque proposition avant de l\'appliquer à vos mesures.')}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-purple-100 hover:text-white hover:bg-purple-700 p-2 rounded-lg transition-colors duration-150"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-4 max-h-[65vh] overflow-y-auto">{renderBody()}</div>

          {!isLoading && !error && proposals.length > 0 && (
            <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-[200px] flex-1">
                  <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all duration-300"
                      style={{ width: `${(decidedCount / proposals.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap">
                    {t('workshop3.activity4.fusion.decided', '{{done}} / {{total}} décidées', { done: decidedCount, total: proposals.length })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {!allDecided && (
                    <span className="text-[11px] text-amber-600">
                      {t('workshop3.activity4.fusion.allDecidedHint', 'Acceptez, modifiez ou refusez chaque proposition avant d\'appliquer.')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!allDecided}
                    title={!allDecided ? t('workshop3.activity4.fusion.allDecidedHint', 'Acceptez, modifiez ou refusez chaque proposition avant d\'appliquer.') : ''}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShieldCheck size={16} />
                    {t('workshop3.activity4.fusion.apply', 'Appliquer la fusion')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

MeasureFusionWorkspace.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  analysisId: PropTypes.string,
  measures: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  onApplyFusion: PropTypes.func.isRequired,
};

MeasureFusionWorkspace.defaultProps = {
  analysisId: null,
  measures: [],
};

export default MeasureFusionWorkspace;
